import React, { useState } from 'react';
import { Alert, Box, Button, Chip, FormHelperText, InputLabel, LinearProgress, Link, MenuItem, Paper, Select, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import { ILookupResponse } from '../../../model/apm/base/lookup-response';
import { IPortalDocumentResponse } from '../../../model/apm/document/portal-document-response';
import { DOCUMENT_ACCEPT, DOCUMENT_MAX_SIZE_MB, fileDisplayName, validateDocumentFile } from './document-utils';
import { DeleteAction } from '../../../components/row-actions';

interface DocumentAttachmentsProps {
    id: string;
    files: IPortalDocumentResponse[];
    documentTypes: ILookupResponse[];
    // Mensagem exibida quando ainda não é possível anexar (ex.: registro não salvo)
    disabledMessage?: string;
    // Somente consulta: lista os anexos sem permitir enviar ou excluir
    readOnly?: boolean;
    onUpload: (documentTypeId: number, file: File, onProgress: (percent: number) => void) => Promise<void>;
    onDelete: (file: IPortalDocumentResponse) => void;
}

// Anexos enviados e excluídos na hora, independentemente do botão Salvar do formulário
export function DocumentAttachments({ id, files, documentTypes, disabledMessage, readOnly, onUpload, onDelete }: DocumentAttachmentsProps) {
    const [documentTypeId, setDocumentTypeId] = useState<number | ''>('');
    const [progress, setProgress] = useState<number>();
    const [error, setError] = useState<string>();
    const [dragActive, setDragActive] = useState(false);
    const uploading = progress !== undefined;

    if (disabledMessage) {
        return <Alert severity="info" icon={<AttachFileIcon />}>{disabledMessage}</Alert>;
    }

    async function handleFile(file?: File) {
        if (!file || uploading) return;
        setError(undefined);
        if (documentTypeId === '') {
            setError('Selecione o tipo de documento antes de escolher o arquivo.');
            return;
        }
        const validationError = validateDocumentFile(file);
        if (validationError) {
            setError(validationError);
            return;
        }
        setProgress(0);
        try {
            await onUpload(documentTypeId, file, setProgress);
        } catch (e: any) {
            setError(e?.message || 'Não foi possível enviar o arquivo.');
        } finally {
            setProgress(undefined);
        }
    }

    function handleDrag(e: React.DragEvent) {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
        else if (e.type === 'dragleave') setDragActive(false);
    }

    function handleDrop(e: React.DragEvent) {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        handleFile(e.dataTransfer.files?.[0]);
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!readOnly && (
                <Box
                    onDragEnter={handleDrag}
                    onDragOver={handleDrag}
                    onDragLeave={handleDrag}
                    onDrop={handleDrop}
                    sx={{
                        p: 2, borderRadius: 1, border: '1px dashed',
                        borderColor: dragActive ? 'primary.main' : 'divider',
                        bgcolor: dragActive ? 'action.hover' : 'background.default',
                    }}
                >
                    <input
                        style={{ display: 'none' }}
                        id={id}
                        type="file"
                        accept={DOCUMENT_ACCEPT}
                        onChange={(e) => {
                            handleFile(e.target.files?.[0]);
                            e.target.value = '';
                        }}
                    />
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                            <InputLabel required>Tipo de documento</InputLabel>
                            <Select
                                fullWidth
                                size="small"
                                value={documentTypeId}
                                disabled={uploading}
                                onChange={(e) => {
                                    setDocumentTypeId(e.target.value === '' ? '' : Number(e.target.value));
                                    setError(undefined);
                                }}
                            >
                                {documentTypes.map((t) => <MenuItem key={t.id} value={t.id}>{t.description}</MenuItem>)}
                            </Select>
                        </Box>
                        <label htmlFor={id}>
                            <Button variant="contained" component="span" disabled={uploading || documentTypeId === ''} startIcon={<CloudUploadIcon />}>
                                Anexar arquivo
                            </Button>
                        </label>
                    </Box>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                        {dragActive ? 'Solte o arquivo para anexar' : `Selecione o tipo e escolha ou arraste o arquivo. PDF, Office/LibreOffice, ZIP/RAR, JPG ou PNG até ${DOCUMENT_MAX_SIZE_MB} MB.`}
                    </Typography>
                    {uploading && <LinearProgress variant="determinate" value={progress} sx={{ mt: 1 }} />}
                    {error && <FormHelperText error>{error}</FormHelperText>}
                    {documentTypes.length === 0 && <FormHelperText>Nenhum tipo de documento disponível para a entidade.</FormHelperText>}
                </Box>
            )}

            {files.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nenhum documento anexado.</Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {files.map((file) => (
                        <Paper key={file.id} variant="outlined" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, pl: 1.5 }}>
                            <DescriptionIcon color="primary" fontSize="small" />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                {file.url ? (
                                    <Link href={file.url} target="_blank" rel="noopener noreferrer" variant="body2" fontWeight={500} noWrap sx={{ display: 'block' }}>
                                        {fileDisplayName(file)}
                                    </Link>
                                ) : (
                                    <Typography variant="body2" fontWeight={500} noWrap>{fileDisplayName(file)}</Typography>
                                )}
                                {file.authenticityCode && (
                                    <Typography variant="caption" color="text.secondary" noWrap component="div">Código: {file.authenticityCode}</Typography>
                                )}
                            </Box>
                            {file.documentType && <Chip size="small" variant="outlined" label={file.documentType} sx={{ maxWidth: 180 }} />}
                            {!readOnly && (
                                <DeleteAction title="Excluir anexo" onClick={() => onDelete(file)} />
                            )}
                        </Paper>
                    ))}
                </Box>
            )}
        </Box>
    );
}
